import { useId, useState } from 'react'
import { Icon } from '../../components/ui/Icon'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Spinner } from '../../components/ui/Spinner'
import { formatMoney } from '../cart/cart-money'
import {
  type ConfigDimensionSegment,
  type SkuPicks,
  computeRangePresets,
  formatValueToken,
  gateConfigSchema,
  isValueLegal,
  validateAndComposeSku,
} from '../catalog/configurator-sku'
import type { ConfigurableResolveService } from '../catalog/useConfigurableResolve'
import { useConfigurableResolve } from '../catalog/useConfigurableResolve'
import { toAddPartsCandidateRow } from './rfq-append-lines'
import type { AddPartsCandidateRow } from './rfq-append-lines'
import { useAddPartsSearch } from './use-add-parts-search'

/**
 * UID-401 N1 (Gate-5) — validate `imageRef` before interpolating it into this
 * row's inline `url(...)`. ADR-0090 D2 only ever authorizes a root-relative
 * `/assets/<file>` path (the demo catalog dataset's own convention for every
 * legitimately-seeded value) — anything else falls back to the existing
 * empty placeholder, the same fallback a missing/`null` `imageRef` already
 * uses.
 *
 * `CatalogResultThumb`'s `<img src>` is the safer in-repo precedent, but this
 * thumbnail is decorative background-fill (the row's own `aria-label`
 * carries the accessible name, per this file's own doc comment below), not
 * informative `<img>` content, and matching the design's
 * `background-size:cover` 38×38/`border-radius:4px` geometry
 * (`dc.html:4699-4713`) via a swapped element would mean re-deriving that
 * geometry with `object-fit` across the shared `rfq.css` rule both this file
 * and `AddPartsToRfqModal.tsx` use. Validating the value is the cheaper fix
 * with the same safety outcome: CSSOM already rejects a syntactically
 * malformed `url()` value on its own (the negligible-exploitability case the
 * ticket notes), but a syntactically VALID absolute URL — e.g. an
 * attacker-hosted tracking pixel — would sail through unvalidated before
 * this check exists.
 */
export function isSafeCatalogImageRef(imageRef: string | null | undefined): imageRef is string {
  return (
    typeof imageRef === 'string' &&
    /^\/assets\/[\w./-]+$/.test(imageRef) &&
    !imageRef.includes('..')
  )
}

/**
 * UID-335 follow-up — a SEPARATE, WIDER predicate for a part's thumbnail
 * specifically (never reused for `ShopByBrand.tsx`'s `logoRef`, which stays
 * on {@link isSafeCatalogImageRef} unchanged): a custom/uploaded part's
 * generated CAD thumbnail lives behind a presigned S3 GET, but that raw
 * absolute URL is NEVER what reaches this check — a first attempt at
 * widening this predicate to accept any `http(s)://` URL was caught by this
 * file's own test suite (`AddPartsModal.test.tsx`) asserting a value like
 * `https://evil.example/tracker.png` must be REJECTED: a malicious absolute
 * URL is syntactically indistinguishable from a legitimate presigned one, so
 * "accept any well-formed https URL" is not actually a safe rule, whatever
 * the value's usual provenance.
 *
 * The real fix instead keeps this SAME-ORIGIN-ONLY invariant intact:
 * `GET /api/uploads/thumbnails/{id}` (`uploads-routes.ts`) is a same-origin
 * redirect to the real presigned URL, resolved and tenant-scoped SERVER-SIDE
 * — `add-parts-search.ts` sets `imageRef` to THIS root-relative path for a
 * custom/uploaded part, never the raw S3 URL. So this predicate only ever
 * needs to accept two root-relative prefixes, never an absolute URL of any
 * kind — the original attacker-hosted-tracking-pixel case stays rejected
 * exactly as before.
 */
export function isSafePartImageRef(imageRef: string | null | undefined): imageRef is string {
  return (
    isSafeCatalogImageRef(imageRef) ||
    (typeof imageRef === 'string' && /^\/api\/uploads\/thumbnails\/[\w-]+$/.test(imageRef))
  )
}

export interface AddPartsModalProps {
  readonly open: boolean
  /** UID-457 item 2 (ADR-0147) — the real, debounced candidate search
   * (`add-parts-search.ts`'s `searchAddPartsCandidates`, composing
   * `GET /catalog/search` + `GET /parts?q=` at this vertical's seam),
   * injected exactly as `verticals/parts/components/AddPartsModal.tsx`
   * takes its own `search` prop from `BomDetailPage`. Called with the
   * debounced query text (empty string on open, before the customer types
   * anything) — this modal owns debounce/loading/abort-on-unmount, not the
   * caller. */
  readonly search: (query: string) => Promise<readonly AddPartsCandidateRow[]>
  /** Sourceable ids already in the review lines — excluded so a part can't
   * be added twice. */
  readonly alreadyAddedIds: ReadonlySet<string>
  readonly onClose: () => void
  readonly onAdd: (selected: readonly AddPartsCandidateRow[]) => void
  /** The design's footer "Upload new files" — closes the modal and hands off
   * to the CAD-intake screen, same as `AddPartsToRfqModal`'s. */
  readonly onUploadNewFiles: () => void
  /** Injectable for tests; defaults to the real BFF-backed
   * `GET /catalog/configurable/{sku}` (`useConfigurableResolve`'s own
   * default) — the seam a `kind: 'configurable'` candidate row's inline
   * "Configure" step resolves through. Mirrors
   * `AddPartsToRfqModal`'s own prop of the same name. */
  readonly configurableResolveService?: ConfigurableResolveService
}

/**
 * The RFQ review screen's "New RFQ request" / "Add parts" entry point
 * (screens.md §15: "Entry point ... lets users pick from library parts
 * needing quote or upload new parts"). Matches the design's "Add parts to
 * this RFQ" picker (`AddPartsToRfqModal`, `Fictiv Platform.dc.html`
 * L3630-3666) — subtitle, search, per-row thumbnail/name/tag/price, a
 * per-row `Add`, and an Upload/Done footer (UID-396) — rather than the
 * earlier plain checkbox-batch list, so the two add-parts surfaces in this
 * app (compose-time here, post-submit in `AddPartsToRfqModal`) look
 * identical per design. `onAdd` still fires with an array (one row at a
 * time) so the compose page's existing batch-shaped handler needs no
 * change.
 *
 * UID-457 item 2 (ADR-0147) — this modal used to offer only a flat, static
 * list of the demo fixtures dataset (`apexAutomationGantry`, UID-56), then a
 * catalog-only live search (UID-457 item 2's first pass). Both candidate
 * populations — catalog AND account library, each tagged — now come from
 * `add-parts-search.ts`'s `searchAddPartsCandidates`, composed client-side
 * over `GET /catalog/search` + `GET /parts?q=` (the `search` prop). See
 * `docs/adr/0147-uid457-add-parts-candidate-union-at-the-vertical-seam.md`
 * for why the union is composed here rather than behind
 * `GET /boms/add-parts/search`.
 */
export function AddPartsModal({
  open,
  search,
  alreadyAddedIds,
  onClose,
  onAdd,
  onUploadNewFiles,
  configurableResolveService,
}: AddPartsModalProps) {
  // Mirrors `verticals/parts/components/AddPartsModal.tsx`'s own reset — on
  // CLOSE, not on open, so a re-open never flashes the previous visit's
  // stale query/results before the debounced effect below refetches.
  const { query, setQuery, state } = useAddPartsSearch({ open, search, resetOn: 'close' })

  if (!open) return null

  const results = state.status === 'ready' ? state.results : []
  const selectable = results.filter((candidate) => !alreadyAddedIds.has(candidate.sourceable.id))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add parts to this RFQ"
      maxWidth={560}
      className="rfq-add-parts-modal"
      headerContent={
        <div>
          <h2 className="modal-title rfq-add-parts-title">Add parts to this RFQ</h2>
          <p className="rfq-add-parts-subtitle">
            Pull in catalog parts or existing library parts, or upload new files.
          </p>
        </div>
      }
      footer={
        <div className="rfq-add-parts-footer">
          <button
            type="button"
            className="rfq-add-parts-upload-btn"
            onClick={() => {
              onUploadNewFiles()
              onClose()
            }}
          >
            <Icon name="upload" /> Upload new files
          </button>
          <button type="button" className="rfq-add-parts-done-btn" onClick={onClose}>
            Done
          </button>
        </div>
      }
    >
      <div className="rfq-add-parts-search">
        <Icon name="search" className="rfq-add-parts-search-icon" />
        {/* Not the shared `Input` primitive: the design draws this as a bare
         * icon-inset search field with NO visible label. The accessible name
         * is carried by `aria-label`, never by the placeholder alone (WCAG
         * 3.3.2 / 4.1.2). */}
        <input
          type="search"
          className="rfq-add-parts-search-input"
          aria-label="Search catalog or library parts"
          placeholder="Search catalog or library parts by name or MPN…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {/* UID-457 item 2 (1a) — loading/error/empty are kept mutually
       * exclusive: the empty copy must never render while a search is in
       * flight or has failed (a stale/absent list must never look like "no
       * matches"). The `cancelled` guard inside `useAddPartsSearch` keeps a
       * late failure for an abandoned query from clobbering a newer one's
       * state. */}
      {state.status === 'loading' && (
        <p className="rfq-add-parts-loading">
          <Spinner size="sm" label="Searching…" />
        </p>
      )}
      {state.status === 'error' && (
        <p className="rfq-add-parts-error" role="alert">
          {state.message}
        </p>
      )}
      {state.status === 'ready' && selectable.length === 0 && (
        <p className="rfq-add-parts-empty">
          No matching parts. Try a different search, or upload new files below.
        </p>
      )}
      {state.status === 'ready' && selectable.length > 0 && (
        <ul className="rfq-add-parts-list">
          {selectable.map((candidate) => {
            const isConfigurable = candidate.sourceable.kind === 'configurable'
            return (
              <li key={candidate.sourceable.id} className="rfq-add-parts-row">
                {isConfigurable ? (
                  <ConfigurableCandidateRow
                    candidate={candidate}
                    onAdd={onAdd}
                    {...(configurableResolveService !== undefined
                      ? { resolveService: configurableResolveService }
                      : {})}
                  />
                ) : (
                  <div className="rfq-add-parts-row-main">
                    {/* UID-401 item 2 (`dc.html:4449`) — a real 38×38 cover-fit
                     * thumbnail when the candidate's Sourceable carries `imageRef`
                     * (ADR-0090 D2). Genuinely decorative here (the row's own
                     * `aria-label="Add {name}"` on the button below already
                     * carries the accessible name), so it stays `aria-hidden`
                     * with an inline `background-image`, NOT the informative
                     * `<img alt=…>` pattern `CatalogResultThumb` uses — that
                     * component's `alt`-bearing treatment is for a row where the
                     * image IS the content; this row's content is the name text
                     * next to it. No `imageRef` falls back to the existing empty
                     * placeholder box — never a fabricated/hard-coded path. */}
                    <span
                      className="rfq-add-parts-thumb"
                      aria-hidden="true"
                      style={
                        isSafePartImageRef(candidate.sourceable.imageRef)
                          ? {
                              // Quoted + only-quote/backslash escaped, not the whole
                              // URL re-encoded — a presigned URL's own `%`-escapes
                              // (signature, date) must survive untouched, or S3
                              // rejects the request (see InProgressSessionCard.tsx's
                              // identical fix, UID-335).
                              backgroundImage: `url("${candidate.sourceable.imageRef.replace(/["\\]/g, '\\$&')}")`,
                            }
                          : undefined
                      }
                    />
                    <span className="rfq-add-parts-meta">
                      <span className="rfq-add-parts-name-row">
                        <span className="rfq-add-parts-name" title={candidate.name}>
                          {candidate.name}
                        </span>
                        {/* UID-457 item 2 (ADR-0147) — the per-row source badge,
                         * matching `AddPartsToRfqModal.tsx`'s already-shipped
                         * convention exactly: `tag` is `'Buy'` or `'Library'`,
                         * derived from which read produced the row, never from the
                         * data itself. */}
                        <span
                          className={`rfq-add-parts-tag rfq-add-parts-tag-${candidate.tag.toLowerCase()}`}
                        >
                          {candidate.tag}
                        </span>
                      </span>
                      <span className="rfq-add-parts-sub">{candidate.partNumber ?? '—'}</span>
                    </span>
                    {/* `dc.html:4454` — the price is its OWN right-aligned
                     * element (`p.priceLabel`), not concatenated into the SKU
                     * subtitle line above. */}
                    <span className="rfq-add-parts-price">
                      {candidate.sourceable.indicativePrice
                        ? formatMoney(
                            candidate.sourceable.indicativePrice.amount,
                            candidate.sourceable.indicativePrice.currency,
                          )
                        : 'Quote required'}
                    </span>
                    {/* Repeated "Add" buttons need a per-row accessible name — a
                     * screen-reader user tabbing the list otherwise hears "Add"
                     * N times with nothing to distinguish them (WCAG 2.4.6). */}
                    <button
                      type="button"
                      className="rfq-add-parts-add-btn"
                      aria-label={`Add ${candidate.name}`}
                      onClick={() => onAdd([candidate])}
                    >
                      Add
                    </button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Modal>
  )
}

/** One `configSchema` dimension segment (`range`/`enum`), plus its INDEX
 * into `schema.segments` — verbatim port of `AddPartsToRfqModal.tsx`'s own
 * local helper of the same name; kept local to this file for the same
 * reason that file gives (a smaller, standalone row-scoped picker, not the
 * full-page `SeriesConfiguratorDrawer` surface). */
function dimensionEntries(
  schema: NonNullable<AddPartsCandidateRow['configSchema']>,
): ReadonlyArray<{ readonly segment: ConfigDimensionSegment; readonly index: number }> {
  return schema.segments
    .map((segment, index) => ({ segment, index }))
    .filter(
      (entry): entry is { segment: ConfigDimensionSegment; index: number } =>
        entry.segment.kind === 'range' || entry.segment.kind === 'enum',
    )
}

/**
 * The compose-time twin of `AddPartsToRfqModal.tsx`'s `ConfigurableCandidateRow`
 * — SAME inline-configure UX (expand in place, pick dimensions, resolve via
 * `useConfigurableResolve`, "Add" appears only once resolved), adapted for
 * this modal's different add mechanism: instead of calling
 * `onAdd(sourceable)` directly against a live append endpoint, this row
 * calls `onAdd([...])` with a NEWLY-BUILT `AddPartsCandidateRow` that wraps
 * the RESOLVED Sourceable (via `toAddPartsCandidateRow`, preserving the
 * original row's `tag`) — the exact same batch-shaped `onAdd` the plain,
 * non-configurable row already uses one line up. The original, UNRESOLVED
 * candidate is never what reaches `onAdd`: an unresolved `configurable`
 * family is not a concrete orderable SKU, so it must never land in the
 * draft/batch selection (mirrors the post-submit modal's own invariant).
 *
 * There is no `adding`/in-flight prop here (unlike `AddPartsToRfqModal`):
 * this modal's `Add` mutates local draft state synchronously rather than
 * awaiting a server POST, so there is nothing to disable against.
 */
function ConfigurableCandidateRow({
  candidate,
  onAdd,
  resolveService,
}: {
  readonly candidate: AddPartsCandidateRow
  readonly onAdd: (selected: readonly AddPartsCandidateRow[]) => void
  /** Injectable for tests; defaults to the real BFF-backed resolve (via
   * `useConfigurableResolve`'s own default). */
  readonly resolveService?: ConfigurableResolveService
}) {
  const [expanded, setExpanded] = useState(false)
  const [picks, setPicks] = useState<SkuPicks>(new Map())
  const inputIdBase = useId()

  const schema = candidate.configSchema
  const gate = schema ? gateConfigSchema(schema) : { kind: 'disabled' as const }
  const entries = schema && gate.kind === 'pickable' ? dimensionEntries(schema) : []

  const sku = expanded && schema ? validateAndComposeSku(schema, picks) : null
  const resolveState = useConfigurableResolve(sku, resolveService)
  const resolvedSourceable = resolveState.status === 'resolved' ? resolveState.sourceable : null

  function pick(index: number, value: number | null) {
    setPicks((prev) => {
      const next = new Map(prev)
      if (value === null) next.delete(index)
      else next.set(index, value)
      return next
    })
  }

  function cancel() {
    setExpanded(false)
    setPicks(new Map())
  }

  const displayPrice = resolvedSourceable?.indicativePrice ?? candidate.sourceable.indicativePrice

  return (
    <>
      <div className="rfq-add-parts-row-main">
        <span
          className="rfq-add-parts-thumb"
          aria-hidden="true"
          style={
            isSafePartImageRef(candidate.sourceable.imageRef)
              ? {
                  backgroundImage: `url("${candidate.sourceable.imageRef.replace(/["\\]/g, '\\$&')}")`,
                }
              : undefined
          }
        />
        <span className="rfq-add-parts-meta">
          <span className="rfq-add-parts-name-row">
            <span className="rfq-add-parts-name" title={candidate.name}>
              {candidate.name}
            </span>
            <span className={`rfq-add-parts-tag rfq-add-parts-tag-${candidate.tag.toLowerCase()}`}>
              {candidate.tag}
            </span>
          </span>
          <span className="rfq-add-parts-sub">
            {resolvedSourceable?.catalogSku ?? candidate.partNumber ?? '—'}
          </span>
          {/* The genuinely-not-configurable-here fallback (no usable
           * `configSchema`) — same tone/copy as `AddPartsToRfqModal.tsx`'s. */}
          {gate.kind === 'disabled' && (
            <span className="rfq-add-parts-configurable-note">
              Configure this part in the catalog before adding
            </span>
          )}
        </span>
        <span className="rfq-add-parts-price">
          {displayPrice
            ? formatMoney(displayPrice.amount, displayPrice.currency)
            : 'Quote required'}
        </span>
        {gate.kind === 'disabled' ? (
          <button
            type="button"
            className="rfq-add-parts-add-btn"
            aria-label={`Add ${candidate.name} — configure this part in the catalog first`}
            disabled
          >
            Add
          </button>
        ) : !expanded ? (
          <button
            type="button"
            className="rfq-add-parts-add-btn"
            aria-label={`Configure ${candidate.name}`}
            onClick={() => setExpanded(true)}
          >
            Configure
          </button>
        ) : (
          <div className="rfq-add-parts-configure-actions">
            <button
              type="button"
              className="rfq-add-parts-configure-cancel-btn"
              aria-label={`Cancel configuring ${candidate.name}`}
              onClick={cancel}
            >
              Cancel
            </button>
            {resolvedSourceable && (
              <button
                type="button"
                className="rfq-add-parts-add-btn"
                aria-label={`Add ${candidate.name}`}
                onClick={() => onAdd([toAddPartsCandidateRow(resolvedSourceable, candidate.tag)])}
              >
                Add
              </button>
            )}
          </div>
        )}
      </div>

      {expanded && schema && gate.kind === 'pickable' && (
        <div className="rfq-add-parts-configure-panel">
          {entries.map(({ segment, index }) => (
            <fieldset key={index} className="rfq-add-parts-configure-field">
              <legend>{segment.fieldName ?? 'Value'}</legend>
              {segment.kind === 'enum' && (
                <div className="rfq-add-parts-configure-pills">
                  {segment.values.map((value) => {
                    const selected = picks.get(index) === value
                    return (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={selected}
                        className={`rfq-add-parts-configure-pill${
                          selected ? ' rfq-add-parts-configure-pill-selected' : ''
                        }`}
                        onClick={() => pick(index, selected ? null : value)}
                      >
                        {formatValueToken(segment, value)}
                      </button>
                    )
                  })}
                </div>
              )}
              {segment.kind === 'range' && (
                <RangeControl
                  segment={segment}
                  label={segment.fieldName ?? 'Value'}
                  idBase={`${inputIdBase}-${index}`}
                  value={picks.get(index) ?? null}
                  onChange={(value) => pick(index, value)}
                />
              )}
            </fieldset>
          ))}

          <div className="rfq-add-parts-configure-status" aria-live="polite">
            {resolveState.status === 'loading' && (
              <span className="rfq-add-parts-configure-checking">
                <Spinner size="sm" aria-hidden="true" />
                Checking availability…
              </span>
            )}
            {resolveState.status === 'not-available' && (
              <p role="alert" className="rfq-add-parts-configure-error">
                Not available at that combination. Try a different value.
              </p>
            )}
            {resolveState.status === 'error' && (
              <p role="alert" className="rfq-add-parts-configure-error">
                {resolveState.message}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  )
}

type RangeSegment = Extract<ConfigDimensionSegment, { kind: 'range' }>

/** The row-scoped range picker — verbatim port of
 * `AddPartsToRfqModal.tsx`'s own `RangeControl` (same
 * `computeRangePresets`/`formatValueToken`/`isValueLegal` legality rules). */
function RangeControl({
  segment,
  label,
  idBase,
  value,
  onChange,
}: {
  readonly segment: RangeSegment
  readonly label: string
  readonly idBase: string
  readonly value: number | null
  readonly onChange: (value: number | null) => void
}) {
  const [raw, setRaw] = useState(value !== null ? formatValueToken(segment, value) : '')
  const presets = computeRangePresets(segment)

  return (
    <div className="rfq-add-parts-configure-range">
      <div className="rfq-add-parts-configure-pills">
        {presets.map((preset) => {
          const selected = value === preset
          return (
            <button
              key={preset}
              type="button"
              aria-pressed={selected}
              className={`rfq-add-parts-configure-pill${
                selected ? ' rfq-add-parts-configure-pill-selected' : ''
              }`}
              onClick={() => {
                setRaw(formatValueToken(segment, preset))
                onChange(preset)
              }}
            >
              {formatValueToken(segment, preset)}
            </button>
          )
        })}
      </div>
      <Input
        label={label}
        hideLabel
        className="rfq-add-parts-configure-input"
        id={idBase}
        type="number"
        inputMode="decimal"
        min={segment.min}
        max={segment.max}
        step={segment.step}
        placeholder={`${formatValueToken(segment, segment.min)}–${formatValueToken(segment, segment.max)}`}
        value={raw}
        error={
          value !== null && !isValueLegal(segment, value)
            ? `Enter a value between ${segment.min} and ${segment.max}`
            : undefined
        }
        onChange={(event) => {
          const text = event.target.value
          setRaw(text)
          const parsed = text.trim() === '' ? null : Number(text)
          onChange(parsed !== null && Number.isFinite(parsed) ? parsed : null)
        }}
      />
    </div>
  )
}
